import React, { useEffect } from 'react'
import { get } from 'lodash'
import FundApiComponent from 'src/components/FundApiComponents'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import Accordion from '../../../components/Common/Accordion'
import Quote from '../../../components/ManagerQuote'
import MorningstarAnalystRating from '../../../components/FundApiComponents/CurrentPrice/components/MorningstarAnalystRating'
import { BLUE_BASE } from '../../../theme/variables/colors'
import InvestmentTeamBanner from '../../../components/InvestmentTeamBanner'
import Html from '../../../components/Html/Html'
import { useSideNavigationContext } from '../../../utils/context/sideNavigationContext'

const TitleTypography = styled(Typography)(({ theme }) => ({
  margin: '0 0 32px !important',
  paddingBottom: '24px',
  borderBottom: `3px solid ${BLUE_BASE}`,
  width: '132px',
  [theme.breakpoints.down('sm')]: {
    margin: '32px 0 0 !important',
  },
}))

const OverviewDescription = styled(Typography)(() => ({
  marginTop: '32px',
}))

const MorningStarContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'grid',
    width: '90vw',
    margin: '20px auto',
  },
}))

const StyledHTML = styled(Html)(({ theme }) => ({
  marginTop: theme.spacing(2),
}))
const StyledBox = styled(Box)(({ theme }) => ({
  margin: `${theme.spacing(4)} 0`,
}))

const boxStyles = { display: 'block' }
const typographyStyle = { fontWeight: 500 }

export default function FundOverview({
  overview_section,
  fund_classes,
  pageContext,
  id,
  management_section_title,
  managers,
  investmentTeamLogo,
  investmentTeamLink,
  html,
  index,
  inceptionDate,
  showOverviewHeadline,
  fundTitle,
}) {
  const { title, manager_quote, inline_section, section } = overview_section

  const fundClass = fund_classes?.find(
    (item) => item?.share_class[0]?.title === pageContext.fundClass?.title
  )

  while (inline_section?.length < 3) {
    inline_section.push('empty column')
  }

  const { createSectionRef } = useSideNavigationContext()

  return (
    <section ref={createSectionRef(index)}>
      <Box>
        {showOverviewHeadline && (
          <TitleTypography id={id} variant="h2">
            {title}
          </TitleTypography>
        )}
        {html && (
          <OverviewDescription variant="body2">
            <Html content={html} />
          </OverviewDescription>
        )}
        {managers && (
          <InvestmentTeamBanner
            managers={managers}
            management_section_title={management_section_title}
            investmentTeamLogo={investmentTeamLogo}
            investmentTeamLink={investmentTeamLink}
          />
        )}

        <Box key="overview-tables" sx={boxStyles}>
          {inline_section?.map((sectionItem, idx) => {
            const api = get(sectionItem, 'api_reference[0]', {})
            if (sectionItem === 'empty column') {
              return <div key={`empty-${idx}`} />
            }
            api.inceptionDate = inceptionDate
            api.fundTitle = fundTitle
            return (
              <Accordion
                title={sectionItem.title}
                key={api?.id}
                data={api?.data}
                type="Overview"
              >
                <FundApiComponent
                  api={api}
                  fundClass={fundClass}
                  title={sectionItem.title}
                />
              </Accordion>
            )
          })}
        </Box>

        <Awards awards={fundClass?.awards} />

        <MorningStarContainer>
          {fundClass && (
            <MorningstarAnalystRating
              morningstar_rating={fundClass.morningstar_rating[0]}
            />
          )}
        </MorningStarContainer>

        {section?.map((sectionItem) => {
          const api = get(sectionItem, 'api_reference[0]', {})
          api.inceptionDate = inceptionDate
          api.fundTitle = fundTitle
          return (
            <Accordion title={sectionItem.title} key={api?.id} type="Overview">
              <FundApiComponent
                api={api}
                fundClass={fundClass}
                title={sectionItem.title}
              />
            </Accordion>
          )
        })}
      </Box>

      {manager_quote && <Quote manager_quote={manager_quote} />}
    </section>
  )
}

function Awards({ awards = [] }) {
  useEffect(() => {
    if (awards.length === 0) return

    document.querySelectorAll('[id^="award-"]').forEach((element) => {
      const awardId = element.id.replace('award-', '')
      const award = awards.find((item) => item.id === awardId)
      if (award) {
        element.querySelectorAll('img').forEach((image) => {
          image.setAttribute('alt', 'Award Image')
          image.setAttribute('aria-label', `Image ${award.title}`)
        })
        element.querySelectorAll('a').forEach((link) => {
          link.setAttribute('title', `Link to: ${award.title}`)
        })
      }
    })
  }, [awards])

  if (awards.length === 0) {
    return null
  }

  return awards.map(({ title, id, product_award_details }) => (
    <StyledBox id={`award-${id}`} key={id}>
      <Typography variant="h3" sx={typographyStyle}>
        {title}
      </Typography>
      <StyledHTML content={product_award_details} />
    </StyledBox>
  ))
}